/**
 * Backend API Service for Cart Messages
 * Handles API calls for cart message configuration
 */

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL!;

/**
 * Cart message data structure
 */
export interface CartMessageData {
  message: string;
  isEnabled: boolean;
  mode: 'normal' | 'warning';
  storeIds: string[];
  lastEdited: Date | null;
  lastAuthor: string;
}

/**
 * Store info for admin selection
 */
export interface StoreInfo {
  legacyId: string;
  displayName: string;
  state: string;
}

/**
 * Get Firebase ID token from current user
 * Requires Firebase Auth to be initialized and user logged in
 */
const getAuthToken = async (): Promise<string> => {
  const { getAuth } = await import('firebase/auth');
  const auth = getAuth();
  if (!auth.currentUser) throw new Error('No authenticated user found');
  return await auth.currentUser.getIdToken();
};

/**
 * Get cart message configuration (admin only)
 */
export const getCartMessage = async (): Promise<CartMessageData | null> => {
  try {
    const token = await getAuthToken();
    const response = await fetch(`${API_BASE_URL}/api/cart-messages`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });

    const result = await response.json();

    if (!response.ok) {
      throw new Error(result.error || `HTTP ${response.status}`);
    }

    if (result.success && result.data) {
      return {
        message: result.data.message,
        isEnabled: result.data.isEnabled,
        mode: result.data.mode,
        storeIds: result.data.storeIds || [],
        lastEdited: result.data.lastEdited ? new Date(result.data.lastEdited) : null,
        lastAuthor: result.data.lastAuthor || '',
      };
    }

    return null;
  } catch (error) {
    console.error('Error fetching cart message:', error);
    throw error;
  }
};

/**
 * Save cart message configuration (admin only)
 */
export const saveCartMessage = async (data: {
  message: string;
  isEnabled: boolean;
  mode: 'normal' | 'warning';
  storeIds: string[];
}): Promise<void> => {
  try {
    const token = await getAuthToken();
    const response = await fetch(`${API_BASE_URL}/api/cart-messages`, {
      method: 'PUT',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    });

    const result = await response.json();

    if (!response.ok) {
      throw new Error(result.error || `HTTP ${response.status}`);
    }
  } catch (error) {
    console.error('Error saving cart message:', error);
    throw error;
  }
};

/**
 * Get available stores for admin selection (admin only)
 */
export const getAvailableStores = async (): Promise<StoreInfo[]> => {
  try {
    const token = await getAuthToken();
    const response = await fetch(`${API_BASE_URL}/api/cart-messages/stores`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });

    const result = await response.json();

    if (!response.ok) {
      throw new Error(result.error || `HTTP ${response.status}`);
    }

    if (result.success && result.data) {
      return result.data;
    }

    return [];
  } catch (error) {
    console.error('Error fetching available stores:', error);
    throw error;
  }
};

/**
 * Get cart message for a specific store (public, for cart display)
 */
export const getCartMessageForStore = async (storeId: string): Promise<{
  message: string;
  mode: 'normal' | 'warning';
} | null> => {
  try {
    const response = await fetch(`${API_BASE_URL}/api/cart-messages/store/${storeId}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    const result = await response.json();

    if (result.success && result.data) {
      return result.data;
    }

    return null;
  } catch (error) {
    console.error('Error fetching cart message for store:', error);
    return null;
  }
};
